/* STARSEED-CRM-012 — admin CRM cutover helpers.
   ════════════════════════════════════════════════════════════════
   The admin lead pages read/write through the platform CRM (capmarkets-api
   `/crm/admin/*` proxy → 008 bridge → `/v1/crm/*`) when the workload-local
   `crm_platform_authority` switch is ON. That switch is STARSEED-CRM-010's
   authority-flip lever: default OFF, flipped only via the human-gated
   procedure recorded on ticket 010, rolled back by flipping it OFF (the
   legacy Mongo paths stay intact until STARSEED-CRM-014).

   Fail-closed reader (feature-flags doctrine FLAG-R-07): ONLY an explicit
   `true` on the DB-backed flag (served on /auth/me as user.feature_flags)
   enables the cutover. No localStorage override, no build-env fallback —
   error, absence, or any non-true value keeps the legacy data path. */

export function isCrmPlatformAuthorityEnabled(user) {
  return user?.feature_flags?.crm_platform_authority === true;
}

/* Map a platform party (admin matrix column, as the redaction module
   returned it — this is presentation mapping, never a visibility widening)
   onto the row shape LeadManagementPage renders. Fields the platform does
   not model (assigned owner, introducer-in-list, source, introducer lock)
   stay undefined and the page renders them as absent. */
export function mapPartyToLeadRow(party) {
  return {
    id: party.id,
    name: party.name,
    email: party.primaryEmail,
    phone: party.primaryPhone,
    company: party.company,
    lead_status: party.stage ?? null,
    capital_band: party.capitalBand ?? "unknown",
    last_meaningful_action_at: party.lastContactAt ?? null,
    kyc_status: party.kycStatus ?? null,
    created_at: party.createdAt,
    updated_at: party.updatedAt,
  };
}

/* Aggregates → the stats shape the status cards read
   ({ by_status, total }). Parties with no pipeline row roll up under the
   platform's 'none' stage, which has no legacy card — surfaced separately
   as `no_stage` so the count isn't silently dropped. */
export function mapAggregatesToStats(aggregates) {
  const by_status = {};
  let no_stage = 0;
  (aggregates?.stageRollup || []).forEach((r) => {
    if (r.stage === "none") {
      no_stage = r.count;
    } else {
      by_status[r.stage] = r.count;
    }
  });
  return { by_status, total: aggregates?.totalParties ?? 0, no_stage };
}

/* Client-side stalled computation (legacy: qualified_lead with no
   meaningful action for 14+ days — routes/managed_leads.py:94-104). The
   platform has no stalled endpoint yet (STARSEED-CRM-019); computed over
   the fetched list. A null last-contact counts as stalled — no recorded
   activity is the attention case, not the healthy one. */
export function computeStalledLeads(rows, now = new Date()) {
  const cutoff = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
  return (rows || []).filter(
    (r) =>
      r.lead_status === "qualified_lead" &&
      (!r.last_meaningful_action_at || r.last_meaningful_action_at < cutoff),
  );
}

/* Latest ownership row = the current introducer (ownershipHistory is
   effective-dated ascending; sorted defensively). Subjects are capmarkets
   user ids (008 external_subject mapping). */
export function currentIntroducerSubject(party) {
  const history = party?.ownershipHistory || [];
  if (history.length === 0) return null;
  const latest = [...history].sort((a, b) =>
    (a.effectiveFrom || "").localeCompare(b.effectiveFrom || ""),
  )[history.length - 1];
  return latest?.ownerExternalSubject ?? null;
}

/* Platform activity → the shape ActivityTimeline renders. The proxy's
   status_change / lead_created activities carry their detail in payload. */
export function mapCrmActivity(activity) {
  const payload = activity.payload || {};
  return {
    id: activity.id,
    activity_type: activity.activityType,
    subject:
      payload.subject ||
      (activity.activityType === "status_change"
        ? `Status: ${payload.fromStage || "—"} → ${payload.toStage || "—"}`
        : activity.activityType.replace(/_/g, " ")),
    description: payload.description || payload.note || "",
    outcome: payload.outcome || "",
    contact_method: payload.contact_method || "",
    created_at: activity.occurredAt,
    created_by_name: payload.changedByName || undefined,
  };
}
